<script setup lang="ts">
import { ref, onMounted, onUnmounted, nextTick } from 'vue';

const { isMobile } = useLayout();

type Props = {
  title?: string;
  iconPath?: string;
  seeMoreUrl?: string;
  cardHeight?: string; // e.g., '100px', '10rem', 'auto'
  gap?: string;
  seeMoreVariant?: 'bordered' | 'arrow-bordered';
  seeMoreText?: string;
  seeMoreTextMobile?: string;
  paddingBottom?: string; // extra space below items (e.g. for buttons that overflow)
};

const {
  title,
  iconPath,
  seeMoreUrl,
  cardHeight = 'auto',
  gap = '8px',
  seeMoreVariant,
  seeMoreText,
  seeMoreTextMobile,
  paddingBottom = '0px',
} = defineProps<Props>();

const emit = defineEmits(['nearEnd']);

const swiperContainer = ref<HTMLElement | null>(null);
const isDragging = ref(false);
const startX = ref(0);
const scrollLeft = ref(0);
const dragged = ref(false);
const showFadeLeft = ref(false);
const showFadeRight = ref(false);

const currentScrollLeft = ref(0);
const currentScrollWidth = ref(0);
const currentClientWidth = ref(0);

const swiperControls = {
  scrollNext,
  scrollPrev,
  currentScrollLeft,
  currentScrollWidth,
  currentClientWidth,
};

function updateScrollMetrics() {
  if (swiperContainer.value) {
    currentScrollLeft.value = swiperContainer.value.scrollLeft;
    currentScrollWidth.value = swiperContainer.value.scrollWidth;
    currentClientWidth.value = swiperContainer.value.clientWidth;
  }
}

// Mouse handlers (desktop only — no mobile, que usa scroll touch nativo
// via `touch-action: pan-x`). No touch, alguns browsers sintetizam mousemove
// com pequenos movimentos de dedo e isso marcava `dragged=true`, fazendo
// o swiper engolir o clique e impedir o preview modal de abrir.
function onMouseDown(e: MouseEvent) {
  if (isMobile.value || !swiperContainer.value) return;
  isDragging.value = true;
  startX.value = e.pageX - swiperContainer.value.offsetLeft;
  scrollLeft.value = swiperContainer.value.scrollLeft;
  swiperContainer.value.style.cursor = 'grabbing';
  swiperContainer.value.style.userSelect = 'none';
  dragged.value = false;
}

function onMouseMove(e: MouseEvent) {
  if (isMobile.value || !isDragging.value || !swiperContainer.value) return;
  e.preventDefault();

  const x = e.pageX - swiperContainer.value.offsetLeft;
  const walk = x - startX.value;

  if (Math.abs(walk) > 5) {
    dragged.value = true;
  }
  swiperContainer.value.scrollLeft = scrollLeft.value - walk;
  updateScrollMetrics();
}

function onMouseUp() {
  if (isMobile.value || !swiperContainer.value) return;
  isDragging.value = false;
  swiperContainer.value.style.cursor = 'grab';
  swiperContainer.value.style.userSelect = 'auto';
  if (dragged.value) {
    setTimeout(() => {
      dragged.value = false;
    }, 50);
  }
}

function onMouseLeave() {
  if (isMobile.value || !swiperContainer.value) return;
  isDragging.value = false;
  swiperContainer.value.style.cursor = 'grab';
  swiperContainer.value.style.userSelect = 'auto';
}

function handleItemClick(event: MouseEvent) {
  if (isMobile.value) return;
  if (dragged.value) {
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
    dragged.value = false;
  }
}

function checkScrollFades(
  el: HTMLElement | null,
  leftFadeRef: Ref<boolean>,
  rightFadeRef: Ref<boolean>,
) {
  if (!el) return;

  const hasOverflow = el.scrollWidth > el.clientWidth;

  leftFadeRef.value = hasOverflow && el.scrollLeft > 0;
  rightFadeRef.value =
    hasOverflow && el.scrollLeft + el.clientWidth < el.scrollWidth;
  updateScrollMetrics();
}

function checkNearEnd(el: HTMLElement | null) {
  if (!el) return;

  const threshold = 200;

  if (el.scrollLeft + el.clientWidth >= el.scrollWidth - threshold) {
    emit('nearEnd');
  }
}

function scrollNext() {
  if (swiperContainer.value) {
    const scrollAmount = swiperContainer.value.clientWidth * 0.4;

    swiperContainer.value.scrollBy({ left: scrollAmount, behavior: 'smooth' });
  }
}

function scrollPrev() {
  if (swiperContainer.value) {
    const scrollAmount = swiperContainer.value.clientWidth * 0.4;

    swiperContainer.value.scrollBy({ left: -scrollAmount, behavior: 'smooth' });
  }
}

let scrollHandler: (() => void) | null = null;
let resizeHandler: (() => void) | null = null;

onMounted(() => {
  nextTick(() => {
    checkScrollFades(swiperContainer.value, showFadeLeft, showFadeRight);
    checkNearEnd(swiperContainer.value);
    updateScrollMetrics();
  });

  scrollHandler = () => {
    checkScrollFades(swiperContainer.value, showFadeLeft, showFadeRight);
    checkNearEnd(swiperContainer.value);
  };

  resizeHandler = () => {
    checkScrollFades(swiperContainer.value, showFadeLeft, showFadeRight);
  };

  swiperContainer.value?.addEventListener('scroll', scrollHandler);
  window.addEventListener('resize', resizeHandler);
});

onUnmounted(() => {
  if (scrollHandler) {
    swiperContainer.value?.removeEventListener('scroll', scrollHandler);
  }
  if (resizeHandler) {
    window.removeEventListener('resize', resizeHandler);
  }
});

defineExpose({
  scrollNext,
  scrollPrev,
  currentScrollLeft,
  currentScrollWidth,
  currentClientWidth,
});
</script>

<template>
  <div class="afiliado-swiper">
    <PerfilOficialSwiperHeader
      v-if="title || seeMoreUrl"
      :gap="gap"
      :icon-path="iconPath"
      :see-more-text="seeMoreText"
      :see-more-text-mobile="seeMoreTextMobile"
      :see-more-url="seeMoreUrl"
      :see-more-variant="seeMoreVariant"
      :swiper-ref="swiperControls"
      :title="title"
    />

    <div
      ref="swiperContainer"
      class="afiliado-swiper__content"
      :style="{ height: cardHeight || 'auto' }"
      @click.capture="handleItemClick"
      @mousedown="onMouseDown"
      @mouseleave="onMouseLeave"
      @mousemove="onMouseMove"
      @mouseup="onMouseUp"
    >
      <div class="afiliado-swiper__items">
        <slot :handle-click="handleItemClick"></slot>
      </div>
    </div>
  </div>
</template>

<style scope lang="postcss">
.afiliado-swiper {
  @apply flex flex-col select-none w-full;
}

.afiliado-swiper__content {
  overflow-x: auto;
  overflow-y: hidden;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
  -ms-overflow-style: none;
  cursor: grab;
  position: relative;
  touch-action: pan-x;
  overscroll-behavior-x: contain;
  min-height: 100px;
  padding-bottom: v-bind(paddingBottom);
}

@media (min-width: 768px) {
  .afiliado-swiper__content {
    min-height: 120px;
  }
}

.afiliado-swiper__content::-webkit-scrollbar {
  display: none;
}

.afiliado-swiper__items {
  display: flex;
  align-items: stretch;
  gap: v-bind(gap);
  justify-content: flex-start !important;
  flex-wrap: nowrap !important;
  flex-shrink: 0;
}

/* Force direct children (.cardWrapper) to a fixed non-growing size
   so the container never redistributes extra space as increased gap */
.afiliado-swiper__items > .cardWrapper {
  box-sizing: border-box;
  flex: 0 0 260px !important;
  width: 260px !important;
  min-width: 260px !important;
  max-width: 260px !important;
}

/* Mobile: faz o swiper encostar na lateral direita */
@media (max-width: 414px) {
  .afiliado-swiper__content {
    margin-right: calc(-1 * var(--container-padding, 16px));
    padding-right: var(--container-padding, 16px);
  }
}

.afiliado-swiper__items .tournament-card {
  box-sizing: border-box;
  flex: 0 0 260px !important;
  width: 260px !important;
  min-width: 260px !important;
  max-width: 260px !important;
  overflow: hidden;
}

.afiliado-swiper__items .tournament-card :deep(img),
.afiliado-swiper__items .tournament-card :deep(picture) {
  box-sizing: border-box;
  width: 100% !important;
  max-width: 100% !important;
  height: auto !important;
  display: block !important;
}
</style>
